// @file Settings Belt Progress Effect store
import { defineStore } from 'pinia'
import { ref } from 'vue'

const useSettingsBeltProgressEffectStore = defineStore('settingsBeltProgressEffect', () => {
  // Mouse tracking state - global coordinates for badge to calculate relative position
  const isHovering = ref(false)
  const globalMouseX = ref(0)
  const globalMouseY = ref(0)

  // 3D tilt effect state (for certificate)
  const rx = ref(0)
  const ry = ref(0)

  // Mouse position as percentage (for glare effect)
  const mx = ref(50)
  const my = ref(50)

  // Glare opacity
  const glareOpacity = ref(0)

  // Throttling state for mouse move performance optimization
  let ticking = false
  let animationFrameId: number | null = null
  let lastEvent: MouseEvent | null = null
  let lastElement: HTMLElement | null = null

  /**
   * Internal function that performs the actual mouse move calculations
   * Called by requestAnimationFrame to ensure smooth 60fps updates
   */
  const updateMousePosition = () => {
    animationFrameId = null

    // Early exit if mouse has left (prevents race condition)
    if (!lastEvent || !lastElement || !isHovering.value) {
      ticking = false
      return
    }

    const rect = lastElement.getBoundingClientRect()
    const mvX = lastEvent.clientX - rect.left
    const mvY = lastEvent.clientY - rect.top
    const w = rect.width
    const h = rect.height

    // Update global mouse position (for badge to calculate its own relative position)
    globalMouseX.value = lastEvent.clientX
    globalMouseY.value = lastEvent.clientY

    // Calculate the rotation on the Y and X axes based on the mouse position.
    // This creates the 3D tilt effect (for certificate).
    ry.value = (mvX / w - 0.5) * -10
    rx.value = (mvY / h - 0.5) * 10

    // Update mouse position as a percentage for the glare's radial gradient.
    mx.value = (mvX / w) * 100
    my.value = (mvY / h) * 100

    // Set opacity to 1 to make the glare effect visible on hover.
    glareOpacity.value = 1

    ticking = false
  }

  /**
   * Handle mouse move with requestAnimationFrame-based throttling
   * This prevents expensive calculations from running more than once per frame (60fps)
   * Improves performance especially on weaker devices
   */
  const handleMouseMove = (event: MouseEvent, element: HTMLElement) => {
    lastEvent = event
    lastElement = element

    if (!ticking) {
      animationFrameId = window.requestAnimationFrame(updateMousePosition)
      ticking = true
    }
  }

  // Handle mouse enter
  const handleMouseEnter = () => {
    isHovering.value = true
  }

  // Handle mouse leave - reset all values and cancel pending updates
  const handleMouseLeave = () => {
    isHovering.value = false
    rx.value = 0
    ry.value = 0
    glareOpacity.value = 0

    // Cancel any pending animation frame to prevent race condition
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId)
      animationFrameId = null
    }

    // Clear cached event/element to prevent stale updates
    lastEvent = null
    lastElement = null
    ticking = false

    // Keep mx/my at last position for smooth transitions
  }

  return {
    // State
    isHovering,
    globalMouseX,
    globalMouseY,
    rx,
    ry,
    mx,
    my,
    glareOpacity,
    // Actions
    handleMouseMove,
    handleMouseEnter,
    handleMouseLeave,
  }
})
export { useSettingsBeltProgressEffectStore }
